/**
 * The Defender board — Microsoft Defender XDR: incidents, alerts, Secure Score
 * and Defender-for-Identity sensor health, read live from Microsoft Graph by the
 * NOVA backend.
 *
 * ## The state that matters most: this board ships dark
 *
 * As of 2026-08-20 the four read-only Graph application permissions it needs —
 * `SecurityIncident.Read.All`, `SecurityAlert.Read.All`,
 * `SecurityEvents.Read.All` and `SecurityIdentitiesHealth.Read.All` — have **not
 * been consented yet**. A colleague grants them later. So the state this page
 * will actually be seen in is not `ok`, and the page is built around that rather
 * than around a happy path it cannot currently reach:
 *
 * - `/defender/permissions` makes **no Graph call at all**, which is why it
 *   answers with no credential, cannot be throttled, and is what drives the
 *   setup panel. That panel names the exact permissions, the exact app
 *   registration, the exact managed identity, and the order the two grants have
 *   to happen in.
 * - Each data panel that cannot read renders its own access panel instead of its
 *   charts. Not an empty axis, not a zero, not a flat line along the bottom: a
 *   403 rendered as zero open incidents would read as a quiet, well-defended
 *   estate, which is the single most misleading thing this board could say
 *   (§5 "unconfigured ≠ empty ≠ zero").
 * - **When consent lands, nothing here changes and nothing ships.** The same
 *   calls start returning 200, a failed build is negative-cached for 30 s rather
 *   than a full TTL, and the panels light up on their own next poll. No frontend
 *   change, no redeploy.
 *
 * All six `access_state` values are handled, and the compiler enforces it:
 * `ACCESS_COPY` in `./status.ts` is typed `Record<DefenderAccessState, …>` off
 * the generated union, so a seventh state added in `nova/defender/models.py`
 * fails the build rather than rendering as a blank card. The three that must not
 * blur are `consent_missing` (a human must grant a permission), `not_provisioned`
 * (the product is not deployed here — a grant would change nothing) and
 * `request_rejected` (NOVA asked wrongly — raise a ticket against NOVA).
 *
 * ## Altitudes
 *
 * - **L0** — the KPI strip: the consent position first, then one tile per panel
 *   that actually read something. A panel that is dark contributes no tile,
 *   because an em dash in a row of real figures reads as "we looked and found
 *   nothing" rather than "we were not allowed to look".
 * - **L1** — four sections: incidents (severity, status, classification,
 *   product, age, trend), alerts (severity, status, trend, product, detection
 *   source, category, technique), Secure Score (meter, trend, worst controls),
 *   identity health (verdict, sensor and issue mixes).
 * - **L2** — every chart's **table twin**: `?data=<chart-id>` opens the exact
 *   buckets behind a chart as a grid, URL-addressable, back-button-closable.
 *
 * ## Why there is no filter row, and no entity drill
 *
 * The design rules ask for one filter row above everything it scopes (§4.3) and
 * it is deliberately absent. None of the five routes takes a window or a
 * dimension parameter, and their windows genuinely differ (incidents 14 days,
 * alerts 7, Secure Score 90, identity health point-in-time). The backend's
 * windows are constants precisely so that no request input ever reaches a Graph
 * `$filter`; a `DateRangePicker` that changed nothing would be a lie, so each
 * panel states its own window in its caption instead.
 *
 * There is likewise no per-user, per-device or per-mailbox view and no search
 * box. The backend publishes no identifier of any kind — breakdowns are
 * aggregated behind a k≥5 floor with sub-floor cohorts folded into an unlabelled
 * `Other` — and no `/defender/*` route takes a person, a device or an id as a
 * parameter. So there is nothing to drill *to*, and no affordance here promises
 * otherwise. Where a fold happened, the panel says so; a silent fold is the §5
 * defect.
 *
 * Nothing on this board is persisted. `@/lib/api/persist.ts` is a
 * deny-by-default allow-list and `["defender", …]` is deliberately absent from
 * it: security incident and alert data does not belong in browser storage, even
 * aggregated.
 */

import { Icon, RefreshCw, ShieldCheck } from "@norican/icons";
import { Alert, Button, Freshness, PageHeader, Skeleton } from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";

import { NAV_SECTIONS } from "@/components/nav-sections";
import {
  useDefenderAlerts,
  useDefenderIdentityHealth,
  useDefenderIncidents,
  useDefenderPermissions,
  useDefenderSecureScore,
} from "@/lib/api/hooks/defender";
import { logger } from "@/lib/logger";

import { DefenderAccessPanel } from "./access-panel";
import { AlertsSection } from "./alerts-section";
import { IdentitySection } from "./identity-section";
import { IncidentsSection } from "./incidents-section";
import { DefenderKpiStrip } from "./kpi-strip";
import { DefenderSetupPanel } from "./not-configured";
import { DEFENDER_PATH, SETUP_ANCHOR } from "./params";
import { SecureScoreSection } from "./secure-score-section";
import {
  type DefenderAccessState,
  type DefenderPanelState,
  formatCount,
  isPanelOk,
} from "./status";

export { DEFENDER_PATH } from "./params";

const SECTION = NAV_SECTIONS.find((section) => section.path === DEFENDER_PATH);

/**
 * `consent_missing` starts out already explained.
 *
 * The setup panel above the sections explains the consent position exhaustively
 * — the four permissions, the two places to grant them, the ~24 h lag — so a
 * panel that is dark for that reason says "same cause" and names what was not
 * measured, rather than repeating the whole remediation list four times. Four
 * identical lists read as four separate faults instead of one grant.
 */
const PRE_EXPLAINED: readonly DefenderAccessState[] = ["consent_missing"];

/** How stale a snapshot has to be before the `Freshness` dot escalates. */
const STALE_MULTIPLIER_MS = 2000;

export function DefenderPage() {
  const queryClient = useQueryClient();

  const permissions = useDefenderPermissions();
  const incidents = useDefenderIncidents();
  const alerts = useDefenderAlerts();
  const secureScore = useDefenderSecureScore();
  const identity = useDefenderIdentityHealth();

  const permissionsData = permissions.data;
  const incidentsData = incidents.data;
  const alertsData = alerts.data;
  const secureScoreData = secureScore.data;
  const identityData = identity.data;

  const refreshing =
    permissions.isFetching ||
    incidents.isFetching ||
    alerts.isFetching ||
    secureScore.isFetching ||
    identity.isFetching;

  /**
   * The four data panels, in the order they render.
   *
   * Collected once so the "already explained" bookkeeping and the setup panel's
   * "how many panels are waiting on this" count are computed from the same list,
   * rather than from two hand-maintained ones that can disagree.
   */
  const panels: readonly { label: string; state: DefenderPanelState | undefined }[] = [
    { label: "Open incidents", state: incidentsData },
    { label: "Alert volume", state: alertsData },
    { label: "Microsoft Secure Score", state: secureScoreData },
    { label: "Identity sensor health", state: identityData },
  ];

  // The setup panel reads what the panels measured on *this* render, which the
  // permissions route may not reflect yet within one page load. Panels that have
  // not answered are excluded rather than counted as healthy.
  const answered = panels.filter(
    (panel): panel is { label: string; state: DefenderPanelState } => panel.state != null,
  );
  const dark = answered.filter((panel) => !isPanelOk(panel.state));

  // The full ask collapses to one line only once every panel has *answered* and
  // every answer is `ok`. Before then there is no evidence the board is lit, and
  // a green line would be a claim rather than a reading.
  const allLive = answered.length === panels.length && dark.length === 0;

  // Which panel gets the full explanation of a given state, and which say "same
  // cause". Computed in render order, before any JSX, so the answer does not
  // depend on where a component happens to be mounted.
  const repeated = new Map<string, boolean>();
  const explained = new Set<DefenderAccessState>(PRE_EXPLAINED);
  for (const panel of answered) {
    const state = panel.state.access_state;
    repeated.set(panel.label, explained.has(state));
    explained.add(state);
  }

  // The most recent read anything managed, so the header's Freshness dot reflects
  // the board rather than whichever route happens to be listed first.
  const collectedAt = [
    permissionsData?.collected_at,
    incidentsData?.collected_at,
    alertsData?.collected_at,
    secureScoreData?.collected_at,
    identityData?.collected_at,
  ]
    .filter((value): value is string => value != null)
    .sort((a, b) => b.localeCompare(a))[0];

  const staleAfterMs = incidentsData
    ? incidentsData.cache_ttl_sec * STALE_MULTIPLIER_MS
    : undefined;

  const refresh = () => {
    logger.debug("[defender] manual refresh requested");
    void queryClient.invalidateQueries({ queryKey: ["defender"] });
  };

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Defender"
        subtitle={
          SECTION?.description ??
          "Microsoft Defender XDR: incidents, alerts, Secure Score and identity sensor health."
        }
        actions={
          <div className="flex items-center gap-4">
            {collectedAt && (
              <Freshness
                label="Graph read"
                since={collectedAt}
                staleAfter={staleAfterMs}
                pulse={refreshing}
              />
            )}
            <Button
              variant="outline"
              tone="neutral"
              startIcon={<Icon icon={RefreshCw} size="sm" />}
              loading={refreshing}
              onClick={refresh}
            >
              Refresh
            </Button>
          </div>
        }
      />

      {permissions.error && (
        <Alert tone="danger" title="Couldn't load the Defender board">
          {permissions.error instanceof Error
            ? permissions.error.message
            : "The NOVA API did not respond. This is a NOVA problem, not a Microsoft Graph one — " +
              "every Graph outcome, including a refused read, arrives as a field on a 200."}
        </Alert>
      )}

      {permissions.isPending && !permissionsData ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} height="7rem" />
            ))}
          </div>
          <Skeleton height="22rem" />
          <Skeleton height="18rem" />
        </div>
      ) : (
        permissionsData && (
          <>
            {/* ── L0: the consent position, then whatever actually read ──── */}
            <DefenderKpiStrip
              permissions={permissionsData}
              incidents={incidentsData}
              alerts={alertsData}
              secureScore={secureScoreData}
              identity={identityData}
            />

            {/* ── The setup panel: the board's primary state today ───────── */}
            {allLive ? (
              // Every panel is reading. The full ask collapses to one line rather
              // than occupying a screen of remediation nobody needs — but the
              // anchor stays, so the KPI tile above still has somewhere to land.
              <section id={SETUP_ANCHOR} className="scroll-mt-6">
                <Alert
                  tone="success"
                  icon={<Icon icon={ShieldCheck} size="sm" />}
                  title="Every permission this board needs has been granted"
                >
                  All {formatCount(permissionsData.required.length)} read-only Graph application
                  permissions are in place and all four panels are reading live.{" "}
                  {permissionsData.status_detail}
                </Alert>
              </section>
            ) : (
              // `panels` gets every panel that has answered, not only the dark
              // ones: the setup panel's "how many panels are waiting on this"
              // figure is a share of the board, and passing only the dark ones
              // would render it as 4 of 4 whatever the truth was.
              <DefenderSetupPanel permissions={permissionsData} panels={answered} />
            )}

            {/* ── L1: incidents ─────────────────────────────────────────── */}
            {incidentsData &&
              (isPanelOk(incidentsData) ? (
                <IncidentsSection incidents={incidentsData} />
              ) : (
                <DefenderAccessPanel
                  panel="Open incidents"
                  state={incidentsData}
                  repeated={repeated.get("Open incidents") ?? false}
                />
              ))}

            {/* ── L1: alerts ────────────────────────────────────────────── */}
            {alertsData &&
              (isPanelOk(alertsData) ? (
                <AlertsSection alerts={alertsData} />
              ) : (
                <DefenderAccessPanel
                  panel="Alert volume"
                  state={alertsData}
                  repeated={repeated.get("Alert volume") ?? false}
                />
              ))}

            {/* ── L1: Microsoft Secure Score ────────────────────────────── */}
            {secureScoreData &&
              (isPanelOk(secureScoreData) ? (
                <SecureScoreSection secureScore={secureScoreData} />
              ) : (
                <DefenderAccessPanel
                  panel="Microsoft Secure Score"
                  state={secureScoreData}
                  repeated={repeated.get("Microsoft Secure Score") ?? false}
                />
              ))}

            {/* ── L1: Defender for Identity ─────────────────────────────── */}
            {identityData &&
              (isPanelOk(identityData) ? (
                <IdentitySection identity={identityData} />
              ) : (
                <DefenderAccessPanel
                  panel="Identity sensor health"
                  state={identityData}
                  repeated={repeated.get("Identity sensor health") ?? false}
                />
              ))}

            {/* The absence is stated rather than left to be noticed (§5). */}
            <Alert tone="info" title="This board has no per-person, per-device or per-mailbox view">
              Every figure above is an aggregate, and that is enforced by the payload rather than by
              this page choosing not to render something: the backend has no field that can hold a
              user, device, mailbox or incident identifier, and no route takes one as a parameter.
              Cohort breakdowns are floored at k≥5 with everything below it folded into a single
              unlabelled &quot;Other&quot; row, and every panel that folds says so.
            </Alert>
          </>
        )
      )}
    </main>
  );
}
